import React, { useState, useEffect } from "react";
import { Modal, message, DatePicker, Input, Table, Empty, Pagination } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import ButtonAlignment from "./Darts/masterConfiguration/userManagement/buttonalignment";
import { Get_Holidays, Add_Update_Holidays, Delete_Holiday, Get_User_Geo_Location, Role_Account_Query } from "../../services/userAPI";
import dayjs from "dayjs";
import "./viewRole.css";
import { Trash2 } from "lucide-react";
import Editicon from "../../Images/editstages.png";
import { SearchOutlined } from "@ant-design/icons";
import PageBreadcrumb from "../../reusableField/PageBreadcrumb";
import BackIcon from "../../Images/backIcon.png";
import HomeIcon from "../../Images/homeIcon.png";

const MarkHoliday = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { accountId, roleId, accountName, name } = location.state || {};
    const [activeTab, setActiveTab] = useState("attendance-daily-norm");
    const [activeAttendanceTab, setActiveAttendanceTab] = useState("holidays");

    // ✅ CORRECT
    const { data: roleData } = useQuery(Role_Account_Query, {
        variables: { accountId: accountId ? parseInt(accountId) : null },
        fetchPolicy: "network-only",
        skip: !accountId,
    });
    const currentRole = roleData?.getAllJobrole?.find((r) => r.id === roleId);
    const safeAccountName = accountName || "";
    const safeRoleName = name || currentRole?.name || "";  // now currentRole exists


    const getActiveTabFromPath = () => {
        if (location.pathname.includes("masterConfiguration")) {
            return "masters-configuration";
        }

        if (location.pathname.includes("user-management")) {
            return "user-management";
        }

        // Both Attendance Daily Norm and Mark Holidays
        // belong under the Attendance Required main tab.
        return "attendance-daily-norm";
    };

    useEffect(() => {
        setActiveTab(getActiveTabFromPath());

        if (location.pathname.includes("mark-holidays")) {
            setActiveAttendanceTab("holidays");
        } else {
            setActiveAttendanceTab("attendance-daily-norm");
        }
    }, [location.pathname]);

    const handleTabChange = (key) => {
        setActiveTab(key);

        if (key === "masters-configuration") {
            navigate("/masterConfiguration", {
                state: {
                    roleId,
                    accountId,
                    name,
                    accountName,
                },
            });
        } else if (key === "user-management") {
            navigate("/user-management", {
                state: {
                    roleId,
                    accountId,
                    name,
                    accountName,
                },
            });
        } else {
            navigate("/attendance-daily-norm", {
                state: {
                    roleId,
                    accountId,
                    name,
                    accountName,
                },
            });
        }
    };

    const [showAddModal, setShowAddModal] = useState(false);
    const [holidayDate, setHolidayDate] = useState(null);
    const [holidayReason, setHolidayReason] = useState("");
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);
    const [geoLocationData, setGeoLocationData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [formErrors, setFormErrors] = useState({ date: '', reason: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5;

    // Fetch holidays
    const { data: holidaysData, loading: holidaysLoading, refetch } = useQuery(Get_Holidays, {
        variables: { accountId: parseInt(accountId) },
        fetchPolicy: "network-only",
        skip: !accountId // Skip automatic execution, we'll call refetch manually
    });

    // Add/Update holiday mutation
    const [addUpdateHoliday, { loading: addLoading }] = useMutation(Add_Update_Holidays);
    const [deleteHolidayMutation, { loading: deleteLoading }] = useMutation(Delete_Holiday);

    const holidays = holidaysData?.getHolidayReason || [];

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredHolidays = holidays.filter((holiday) => {
        if (!normalizedSearch) return true;
        const reason = (holiday.reason || '').toLowerCase();
        const formattedDate = dayjs(holiday.exceptionDate).format("DD/MM/YYYY").toLowerCase();
        const typeLabel = holiday.isWorking ? "working" : "holiday";
        return (
            reason.includes(normalizedSearch) ||
            formattedDate.includes(normalizedSearch) ||
            typeLabel.includes(normalizedSearch)
        );
    });

    const paginatedHolidays = filteredHolidays.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handleAddHoliday = async () => {
        const errors = { date: '', reason: '' };
        let hasError = false;

        if (!holidayDate) {
            errors.date = "Date is required";
            hasError = true;
        }
        if (!holidayReason.trim()) {
            errors.reason = "Reason is required";
            hasError = true;
        }

        setFormErrors(errors);

        if (hasError) {
            return;
        }

        try {
            const formattedDate = dayjs(holidayDate).format("YYYY-MM-DD");
            const response = await addUpdateHoliday({
                variables: {
                    id: editingHoliday ? parseInt(editingHoliday.id) : null,
                    accountId: parseInt(accountId),
                    exceptionDate: formattedDate,
                    reason: holidayReason.trim(),
                    isWorkingDay: false
                }
            });

            // Check if the mutation returned a failure response
            if (response.data?.addUpdateHolidays?.success === false) {
                message.error(response.data.addUpdateHolidays.message || "Failed to add holiday");
                return;
            }

            message.success(editingHoliday ? "Holiday updated successfully" : "Holiday added successfully");
            handleCloseModal();
            refetch();
        } catch (error) {
            message.error(error.message || "Failed to add holiday");
        }
    };

    const handleBackToViewRole = () => {
        navigate("/experience-configuration", {
            state: {
                roleId,
                accountId,
                name,
                accountName,
            },
        });
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setHolidayDate(null);
        setHolidayReason("");
        setEditingHoliday(null);
        setFormErrors({ date: '', reason: '' });
    };

    const handleOpenAddModal = () => {
        setEditingHoliday(null);
        setHolidayDate(null);
        setHolidayReason("");
        setFormErrors({ date: '', reason: '' });
        setShowAddModal(true);
    };

    const handleEditHoliday = (record) => {
        setEditingHoliday(record);
        setHolidayDate(dayjs(record.exceptionDate));
        setHolidayReason(record.reason || "");
        setFormErrors({ date: '', reason: '' });
        setShowAddModal(true);
    };

    const handleDeleteClick = (record) => {
        setHolidayToDelete(record);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!holidayToDelete?.id) {
            message.error("Unable to delete: missing holiday id");
            return;
        }
        try {
            await deleteHolidayMutation({
                variables: {
                    holidayId: parseInt(holidayToDelete.id),
                    accountId: parseInt(accountId)
                }
            });
            message.success("Holiday deleted successfully");
            setShowDeleteModal(false);
            setHolidayToDelete(null);
            refetch();
        } catch (error) {
            message.error("Failed to delete holiday: " + error.message);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setHolidayToDelete(null);
    };

    const handleDiscardClick = () => {
        navigate("/view-role", {
            state: { accountId, roleId, accountName, name }
        });
    };

    // const handleExportCSV = () => {
    //     if (!filteredHolidays.length) {
    //         message.warning("No holidays to export");
    //         return;
    //     }
    //     const headers = ["S.No", "Date", "Reason"];
    //     const rows = filteredHolidays.map((holiday, index) => [
    //         index + 1,
    //         dayjs(holiday.exceptionDate).format("DD/MM/YYYY"),
    //         `"${(holiday.reason || '').replace(/"/g, '""')}"`
    //     ]);
    //     const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    //     const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    //     const url = URL.createObjectURL(blob);
    //     const link = document.createElement("a");
    //     link.href = url;
    //     link.download = `Mark_Holidays_${dayjs().format("YYYY-MM-DD")}.csv`;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //     URL.revokeObjectURL(url);
    // };

    const columns = [
        {
            title: "S.No",
            key: "sno",
            width: "20%",
            render: (_, __, index) =>
                (currentPage - 1) * pageSize + index + 1
        },
        {
            title: "Date",
            dataIndex: "exceptionDate",
            key: "exceptionDate",
            width: "30%",
            render: (date) => dayjs(date).format("DD/MM/YYYY")
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: "35%"
        },
        {
            title: "Actions",
            key: "actions",
            width: "25%",
            render: (_, record) => (
                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        justifyContent: "flex-end"
                    }}
                >
                    <button
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        onClick={() => handleEditHoliday(record)}
                    >
                        <img
                            src={Editicon}
                            alt="Edit"
                            style={{ width: "18px", height: "18px" }}
                        />
                    </button>

                    <button
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        onClick={() => handleDeleteClick(record)}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="user-profile-container mark-holiday-page">

            {/* BACK / HOME BUTTONS */}
            <div className="flex justify-between items-start mb-2 ml-[-6.5px]">
                {/* ✅ FIXED: was navigate(-1) */}
                <button
                    onClick={() =>
                        navigate("/user-management", {
                            state: {
                                name,
                                accountName,
                                roleId,
                                accountId,
                            },
                        })
                    }
                    className="flex items-center gap-2 bg-[#C21A17] text-white px-5  py-2 rounded-lg text-sm font-semibold shadow-md cursor-pointer"
                    style={{
                        marginLeft: "9px",
                    }}
                >
                    <img
                        src={BackIcon}
                        alt="Back"
                        className="w-[15px] h-[15px] object-contain transition-transform duration-300 hover:scale-105"
                    />
                    Back
                </button>

                <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md cursor-pointer"
                    style={{
                        marginLeft: "auto",
                        // marginRight: "24px",
                    }}
                >
                    <img
                        src={HomeIcon}
                        alt="Home"
                        className="w-[15px] h-[15px]  object-contain transition-transform duration-300 hover:scale-105"
                    />
                    Home
                </button>
            </div>

            {/* BREADCRUMB */}
            <div
                className="flex items-center text-sm text-[#939393]"
                style={{ marginTop: 12, marginLeft: "7px" }}
            >
                <span
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer"
                >
                    {safeAccountName}
                </span>

                <span className="mx-2 text-[14px]">›</span>

                <span
                    onClick={() =>
                        navigate("/admin", {
                            state: { roleId, accountId, name, accountName },
                        })
                    }
                    className="cursor-pointer"
                >
                    {safeRoleName}
                </span>

                <span className="mx-2 text-[14px]">›</span>

                <span
                    onClick={() =>
                        navigate("/experience-configuration", {
                            state: { roleId, accountId, name, accountName },
                        })
                    }
                    className="cursor-pointer"
                >
                    Experience Configuration
                </span>

                <span className="mx-2 text-[14px]">›</span>

                <span className="font-semibold text-gray-800">
                    Masters Configuration
                </span>

            </div>


            {/* TOP NAVIGATION */}
            <ButtonAlignment
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onTabChange={handleTabChange}
                hideHolidays={true}
            />


            {/* ATTENDANCE REQUIRED SUB NAVIGATION */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "8px",
                    marginBottom: "20px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        width: "295px",
                        borderBottom: "1px solid #D9D9D9",
                    }}
                >

                    {/* ATTENDANCE & DAILY NORM */}
                    <button
                        type="button"
                        onClick={() => {
                            setActiveAttendanceTab("attendance-daily-norm");

                            navigate("/attendance-daily-norm", {
                                state: {
                                    roleId,
                                    accountId,
                                    name,
                                    accountName,
                                },
                            });
                        }}
                        style={{
                            flex: 1,
                            background: "transparent",
                            border: "none",
                            borderBottom: `2px solid ${activeAttendanceTab === "attendance-daily-norm" ? "#C21A17" : "transparent"}`,
                            color: activeAttendanceTab === "attendance-daily-norm" ? "#C21A17" : "#333333",
                            padding: "0 0 12px 0",
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: activeAttendanceTab === "attendance-daily-norm" ? "600" : "500",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Attendance & Daily Norm
                    </button>


                    {/* MARK HOLIDAYS */}
                    <button
                        type="button"
                        onClick={() => {
                            setActiveAttendanceTab("holidays");

                            navigate("/mark-holidays", {
                                state: {
                                    roleId,
                                    accountId,
                                    name,
                                    accountName,
                                },
                            });
                        }}
                        style={{
                            flex: 1,
                            background: "transparent",
                            border: "none",
                            borderBottom: `2px solid ${activeAttendanceTab === "holidays" ? "#C21A17" : "transparent"}`,
                            color: activeAttendanceTab === "holidays" ? "#C21A17" : "#333333",
                            padding: "0 0 12px 0",
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: activeAttendanceTab === "holidays" ? "600" : "500",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Mark Holidays
                    </button>

                </div>
            </div>


            {/* MARK HOLIDAYS CONTENT */}
            <div className="product-selection-container">

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                        marginTop: "25px",
                    }}
                >
                    <span
                        className="title"
                        style={{ margin: 0 }}
                    >
                        Mark Holidays
                    </span>
                </div>

                <div className="flex items-center gap-3 font-inter mb-3">

                    <Input
                        placeholder="Search holidays..."
                        suffix={
                            <SearchOutlined
                                style={{ color: "#999" }}
                            />
                        }
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm(e.target.value)
                        }
                        className="flex-1 h-9"
                    />

                    <button
                        className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-2 rounded-[6px] hover:bg-[#AD0101] transition-colors whitespace-nowrap"
                        onClick={handleOpenAddModal}
                    >
                        Add Mark Holidays
                    </button>

                </div>


                {/* HOLIDAYS TABLE */}
                <div
                    style={{
                        background: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                >
                    {holidaysLoading ? (
                        <div
                            style={{
                                textAlign: "center",
                                padding: "40px",
                            }}
                        >
                            Loading holidays...
                        </div>
                    ) : holidays.length > 0 ? (
                        <>
                            <Table
                                columns={columns}
                                dataSource={paginatedHolidays}
                                rowKey={(record, index) =>
                                    record.id ||
                                    `${record.exceptionDate}-${index}`
                                }
                                pagination={false}
                                style={{
                                    fontFamily: "'Inter', sans-serif",
                                }}
                            />

                        </>
                    ) : (
                        <Empty description="No holidays found" />
                    )}
                </div>

                {!holidaysLoading && holidays.length > 0 && filteredHolidays.length > 0 && (
                    <div
                        className="holiday-pagination"
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "16px",
                            padding: "0 0px 12px",
                        }}
                    >
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredHolidays.length}
                            onChange={setCurrentPage}
                            showSizeChanger={false}
                        />
                    </div>
                )}




                <Modal
                    title={editingHoliday ? "Edit Holiday" : "Add Holiday"}
                    open={showAddModal}
                    onCancel={handleCloseModal}
                    footer={
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                            <button
                                onClick={handleCloseModal}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddHoliday}
                                disabled={addLoading}
                                className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-2 rounded-lg hover:bg-[#AD0101] transition-colors"
                            >
                                {addLoading ? (
                                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                ) : (
                                    editingHoliday ? "Update" : "Add"
                                )}
                            </button>
                        </div>
                    }
                    centered
                >


                    <div style={{ marginBottom: "16px" }}>
                        <label
                            style={{
                                display: "block",
                                marginBottom: "8px",
                                fontWeight: 500,
                                fontFamily: "'Inter', sans-serif",
                            }}
                        >
                            Date <span style={{ color: "#AD0101" }}>*</span>
                        </label>

                        <DatePicker
                            value={holidayDate}
                            onChange={(date) => {
                                setHolidayDate(date);
                                if (formErrors.date) {
                                    setFormErrors((prev) => ({ ...prev, date: "" }));
                                }
                            }}
                            format="DD/MM/YYYY"
                            style={{ width: "100%" }}
                            placeholder="Select date"
                            disabledDate={(current) => {
                                return (
                                    current &&
                                    (
                                        current < dayjs("2026-01-01") ||
                                        current > dayjs("2027-12-31")
                                    )
                                );
                            }}
                        />

                        {formErrors.date && (
                            <div
                                style={{
                                    color: "#AD0101",
                                    fontSize: "12px",
                                    marginTop: "6px",
                                }}
                            >
                                {formErrors.date}
                            </div>
                        )}
                    </div>
                    <div>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: 500,
                            fontFamily: "'Inter', sans-serif"
                        }}>
                            Reason <span style={{ color: "#AD0101" }}>*</span>
                        </label>
                        <Input
                            value={holidayReason}
                            onChange={(e) => {
                                setHolidayReason(e.target.value);
                                if (formErrors.reason) {
                                    setFormErrors((prev) => ({ ...prev, reason: '' }));
                                }
                            }}
                            placeholder="Enter holiday reason"
                        />
                        {formErrors.reason && (
                            <div style={{ color: "#AD0101", fontSize: "12px", marginTop: "6px" }}>
                                {formErrors.reason}
                            </div>
                        )}
                    </div>
                </Modal>

                <Modal
                    title="Delete Holiday"
                    open={showDeleteModal}
                    onCancel={handleCancelDelete}
                    footer={[
                        <div className="flex justify-end gap-3">
                            <button
                                key="cancel-delete"
                                onClick={handleCancelDelete}
                                        className="flex items-center gap-2 px-2 py-1 border border-gray-300 text-gray-700 rounded-[6px] text-[14px] hover:bg-gray-50 transition-colors"                            >
                                Cancel
                            </button>
                            <button
                                key="confirm-delete"
                                onClick={handleConfirmDelete}
                                className="flex items-center gap-2 bg-[#AD0101] text-white px-2 py-1 rounded-[6px] hover:bg-[#B00000] transition-colors"
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    ]}
                    centered
                >
                    <p style={{ marginBottom: 0 }}>
                        Are you sure you want to delete the holiday on {holidayToDelete ? dayjs(holidayToDelete.exceptionDate).format("DD/MM/YYYY") : ""}?
                    </p>
                </Modal>
            </div>
        </div >
    );
};

export default MarkHoliday;
